import { useState, useEffect } from 'react';
import { getAuth, getStore } from "../firebase";
import { API_URL } from "../constants";
import { getDateTime, getEventUrlFromTitleDate, moment } from "../utils/date";
import { deleteFileFromURL } from "./storage";
import { getEventGroups } from './config';
import { getAllEventTypes, getEventTypeById } from './eventType';
import { routes } from '../routes';

const EVENTS = "events";

export interface EventDoc {
  ageRangeFrom: number
  ageRangeTo: number
  archived: boolean
  category: string[]
  city: string
  banner: string
  createdAt: string | Date
  dateTime: string | Date
  description: string
  designMyNightId: string
  discountCodes: {
    code: string
    percent: number
  }[]
  eventTypeId: string
  featured: boolean
  howItWorks: {
    col1: {
      description: string
      imgURL: string
    }
    col2: {
      description: string
      imgURL: string
    }
    col3: {
      description: string
      imgURL: string
    }
    finalNotes: string
  }
  id: string
  imgMobileUrl: string
  imgURL: string
  isSameGender?: boolean
  lowercaseTitle: string
  menAvailableTickets: number
  notifiedUsers: boolean
  participants: any
  showBanner: boolean
  ticketPrice: number
  ticketPriceWithoutDiscount?: number
  title: string
  venue: string
  venueAddress?: string
  venueDescription?: string
  votingPeriodEnded?: boolean
  womenAvailableTickets: number
}

export interface Event extends EventDoc {
  createdAt: Date,
  dateTime: Date,
}

export const useFetchEvents = (): [Event[], boolean] => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const events = await getAllEvents();
        if (events.length) {
          setEvents(events);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    }
    loadEvents();
  }, []);

  return [ events, loading ];

}

export const addEvent = async (event) => {
  const { db, addDoc, collection } = await getStore();
  return addDoc(collection(db, EVENTS), {
    createdAt: new Date(),
    ...event,
  });
}

export const getEvent = async (id): Promise<Event> => {
  if (!id) return;
  const { db, doc, getDoc } = await getStore();
  const docRef = doc(db, EVENTS, id);
  const eventDoc = await getDoc(docRef);
  if (!eventDoc.exists()) {
    throw new Error(`Event ${id} not found`);
  }
  const eventData = eventDoc.data() as EventDoc;
  if (!eventData.dateTime) {
    throw new Error(`dateTime not found in event ${eventData.id}`)
  }
  return ({
    ...eventData,
    id: docRef.id,
    dateTime: getDateTime(eventData.dateTime),
    createdAt: getDateTime(eventData.createdAt),
  })
};

export const getAllEvents = async () => {
  try {
    const { db, query, collection, orderBy, getDocs } = await getStore();
    const q = query(collection(db, EVENTS), orderBy("dateTime", "asc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const eventData = doc.data() as EventDoc;
      return {
        ...eventData,
        id: doc.id,
        dateTime: getDateTime(eventData.dateTime),
        createdAt: getDateTime(eventData.createdAt),
      } as Event;
    });
  } catch (e) {
    return ["something"];
  }
};

export const getDynamicFilters = async () => {
  try {
    const { db, query, collection, where, getDocs } = await getStore();
    const q = query(
      collection(db, EVENTS),
      where("archived", "in", [false, null])
    );
    const querySnapshot = await getDocs(q);

    const [ageRangesSet, locationsSet] = querySnapshot.docs.reduce((acc, doc) => {
      const eventData = doc.data();
      acc[0].add(`${eventData.ageRangeFrom}${eventData.ageRangeTo ? ' - ' + eventData.ageRangeTo : '+'}`);
      acc[1].add(eventData.venue);
      return acc;
    }, [new Set<string>(), new Set<string>()]);

    return  {
      locations: Array.from(locationsSet).sort(),
      ages: Array.from(ageRangesSet).sort()
    }
  } catch (e) {
    return {
      locations: [],
      ages: [],
    }
  }
}

export const getLocations = async () => {
  const { locations } = await getDynamicFilters();
  return locations;
};

export const getAges = async () => {
  const { ages } = await getDynamicFilters();
  return ages;
};

export type EventsQueries = {
  ageFrom?: number | null,
  ageTo?: number | null,
  type?: string[] | null,
  date?: string | null,
  day?: number | null,
  city?: string | null,
  location?: string | null,
  showGayEvents?: boolean | null,
  showChristianEvents?: boolean | null,
  showSpeedEvents?: boolean | null,
}

export type EventSearchData = {
  id: string
  imgURL: string; 
  title: string; 
  venue: string;
  venueAddress: string;
  city: string;
  category: string[];
  ageRangeFrom: number; 
  ageRangeTo: number; 
  menAvailableTickets: number; 
  womenAvailableTickets: number; 
  ticketPrice: number; 
  ticketPriceWithoutDiscount: number; 
  featured: boolean; 
  isSameGender: boolean; 
  dateTime: string; 
  createdAt: string;
}

export const getEventsCardData = async (limitAmount, queries:EventsQueries = {}): Promise<EventSearchData[]> => {
  const { db, query, collection, where, orderBy, getDocs, limit } = await getStore();
  let q = query(
    collection(db, EVENTS),
    where("archived", "in", [false, null]),
    orderBy("dateTime", "asc")
  );

  if (Object.keys(queries).length == 0 && limitAmount > 0) {
    q = query(q, limit(limitAmount));
  }

  const querySnapshot = await getDocs(q);
  let results = querySnapshot.docs.map((doc) => {
    const eventData = doc.data() as EventDoc;
    return {
      imgURL: eventData.imgURL,
      title: eventData.title,
      venue: eventData.venue,
      venueAddress: eventData.venueAddress ?? "",
      city: eventData.city ?? "",
      category: eventData.category ?? null,
      ageRangeFrom: Number(eventData.ageRangeFrom),
      ageRangeTo: Number(eventData.ageRangeTo),
      menAvailableTickets: eventData.menAvailableTickets,
      womenAvailableTickets: eventData.womenAvailableTickets,
      ticketPrice: eventData.ticketPrice,
      ticketPriceWithoutDiscount: eventData.ticketPriceWithoutDiscount ?? null,
      featured: eventData.featured,
      isSameGender: eventData?.isSameGender ?? false,
      id: doc.id,
      dateTime: getDateTime(eventData.dateTime),
      createdAt: getDateTime(eventData.createdAt),
    } as EventSearchData;
  });

  if (Object.keys(queries).length > 0) {
    results = results.filter((event) => {
      const filterResults = [];
      if (queries.ageFrom || queries.ageTo) {
        // Allow for ranges to have an unspecified end
        const queryRangeFrom = queries.ageFrom || 18;
        const queryRangeTo = queries.ageTo || Infinity;
        
        // Event might have a single-point age (ageRangeFrom only)
        const eventRangeFrom = event.ageRangeFrom || 18;
        const eventRangeTo = event.ageRangeTo || Infinity;
        
        // Check for overlap between ranges
        // Two ranges overlap if:
        // - One range's start is less than or equal to the other's end AND
        // - One range's end is greater than or equal to the other's start
        const rangesOverlap = 
          eventRangeFrom <= queryRangeTo && 
          queryRangeFrom <= eventRangeTo;
        
        filterResults.push(rangesOverlap);
      }

      if (queries.type && event.category && Array.isArray(event.category)) {
        filterResults.push(event.category.filter(cat => queries.type.includes(cat)));
      }
      if (queries.date) {
        filterResults.push(
          new Date(event.dateTime).toLocaleDateString() ===
            new Date(queries.date).toLocaleDateString()
        );
      }
      if (!queries.date && queries.day) {
        const eventDayNumber = moment(event.dateTime).day();
        filterResults.push(eventDayNumber === Number(queries.day));
      }
      if (queries.city) {
        if (event.city) {
          filterResults.push(event.city === queries.city);
        } else {
          filterResults.push(
            event.venueAddress.toLowerCase().includes(queries.city.toLowerCase())
          );
        }
      }

      if (queries.location) {
        filterResults.push(
          event.venue.toLowerCase().includes(queries.location.toLowerCase())
        );
      }
      // TEMPORARY, while events are gradually updated with the right category - check for category in filters and match to other propertieus
      if (queries.showGayEvents || queries.type?.includes("gay-singles-party")) {
        filterResults.push(event.isSameGender);
      }
      if (queries.showGayEvents || queries.type?.includes("singles-party")) {
        filterResults.push(event.title.indexOf("Singles Party") > -1);
      }
      if (queries.showChristianEvents || queries.type?.includes("christian-speed-dating")) {
        filterResults.push(event.title.indexOf("Christian") > -1);
      }
      return filterResults.every((result) => result);
    });
  }

  results.sort((a, b) => {
    return a.featured === b.featured ? 0 : a.featured ? -1 : 1;
  });

  return limitAmount ? results.slice(0, limitAmount) : results;
};

export const getActiveEvents = async () => {
  const { db, query, collection, where, orderBy, getDocs } = await getStore();
  const q = query(
    collection(db, EVENTS),
    where("archived", "in", [false, null]),
    orderBy("dateTime", "asc")
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {

    const eventData = doc.data() as EventDoc;
    if (!eventData.dateTime) {
      throw new Error(`dateTime not found in event ${eventData.id}`)
    }
    return {
      ...eventData,
      id: doc.id,
      dateTime: getDateTime(eventData.dateTime),
      createdAt: getDateTime(eventData.createdAt),
    };
  });
};

export const updateEvent = async ({ id, ...eventDetails }) => {
  const { db, doc, updateDoc } = await getStore();
  return updateDoc(doc(db, EVENTS, id), eventDetails);
}

export const deleteEvents = async (events) => {
  if (!events) return;
  const { db, doc, getDoc, writeBatch } = await getStore();
  const batch = writeBatch(db);

  // redirect event pages to their related category page on delete
  const redirects = [];
  for (const event of events) {
    if (event.eventTypeId) {
      const eventType = await getEventTypeById(event.eventTypeId);
      if (eventType) {
        const eventUrl = getEventUrlFromTitleDate(event.title, event.dateTime, { absolute: true });
        const redirectUrl = `/${eventType.slug}`;
        redirects.push({
          match: eventUrl,
          to: redirectUrl,
        });
      }
    } 
  }
  const redirectsRef = doc(db, "config", "redirects");
  const redirectsDoc = await getDoc(redirectsRef);
  if (redirectsDoc.exists()) {
    const redirectsData = redirectsDoc.data();
    const newRedirects = [...redirectsData.rules, ...redirects];
    batch.update(redirectsRef, { rules: newRedirects });
  }

  // delete events and their images
  events.forEach((event) => {
    const { id, imgUrl } = event;
    const eventRef = doc(db, EVENTS, id);
    if (eventRef) {
      batch.delete(eventRef);
      if (imgUrl) {
        deleteFileFromURL(imgUrl);
      }
    }
  });

  return batch.commit();
};

export const archiveEvents = async (events) => {
  if (!events) return;

  return events.forEach((event) => {
    event.archived = true;
    updateEvent({ id: event.id, ...event });
  });
};

export const unarchiveEvents = (events) => {
  if (!events) return;

  events.forEach((event) => {
    event.archived = false;
    updateEvent({ id: event.id, ...event });
  });
};

export const addUsersToEvent = (users: {email: string}[], eventId: string) =>
  Promise.all(
    users.map((user) =>
      fetch(`${API_URL}/inviteUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user,
          eventId,
        }),
      })
    )
  ).catch((err) => console.log(err));

export const serializeEvent = (event: Event): EventDoc => {
  return {
    ...event,
    dateTime: event.dateTime.toISOString(),
    createdAt: event.createdAt.toISOString(),
  }
}

export const unserializeEvent = (event: EventDoc): Event => {
  return {
    ...event,
    dateTime: getDateTime(event.dateTime),
    createdAt: getDateTime(event.createdAt),
  }
}